import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import {
    applyConnectNetsByDescriptor,
    applyDeleteSubjects,
    applyDisconnectPinsByDescriptor,
} from "../actions/deleteSubjectsActions";
import {applyAddRouteSegment, applyCommitMultiRoutingUpdate, applyUpdateRouteSegments} from "../actions/routeActions";
import {performDocumentMutation} from "../documentMutations";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type SubjectSliceState = Pick<
    DocumentStoreState,
    | "addRouteSegment"
    | "updateRouteSegments"
    | "commitMultiRoutingUpdate"
    | "deleteSubjectsAction"
    | "disconnectPinsByDescriptor"
    | "connectNetsByDescriptor"
>;

export function createSubjectSlice(deps: SliceDependencies): SubjectSliceState {
    const {primitives, get} = deps;

    return {
        addRouteSegment: (addedNodes, deletedNodes, updatedNodePositions) => {
            performDocumentMutation(
                primitives,
                (draft) => applyAddRouteSegment(draft, addedNodes, deletedNodes, updatedNodePositions),
                {
                    actionType: "addRouteSegment",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        updateRouteSegments: (updates) => {
            performDocumentMutation(primitives, (draft) => applyUpdateRouteSegments(draft, updates), {
                actionType: "updateRouteSegment",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        commitMultiRoutingUpdate: (addedNodes, deletedNodes, updatedNodes) => {
            performDocumentMutation(
                primitives,
                (draft) => applyCommitMultiRoutingUpdate(draft, addedNodes, deletedNodes, updatedNodes),
                {
                    actionType: "commitMultiRoutingUpdate",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },

        deleteSubjectsAction: (subjectUids, vertexUpdates = [], deselectUids, subscriptionKey) => {
            if (!subjectUids || subjectUids.length === 0) {
                if (!vertexUpdates || vertexUpdates.length === 0) {
                    return;
                }
            }
            const document = get().document;
            if (
                !document?.elements ||
                !document?.routes ||
                !document?.pcbLayoutRuleSets ||
                !document?.routeVertices ||
                !document?.uid ||
                !document?.pcbLayoutNodes
            ) {
                return;
            }
            try {
                performDocumentMutation(
                    primitives,
                    (draft) => applyDeleteSubjects(draft, subjectUids, vertexUpdates, deselectUids),
                    {
                        actionType: "deleteSubjects",
                        canUndo: true,
                        shouldGenerateActionRecord: true,
                        updatesDocumentTimestamp: true,
                        additionalPayload: subscriptionKey ? {subscriptionKey} : undefined,
                    },
                );
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },

        disconnectPinsByDescriptor: (pinDescriptors) => {
            if (!pinDescriptors || pinDescriptors.length === 0) {
                return;
            }
            try {
                performDocumentMutation(primitives, (draft) => applyDisconnectPinsByDescriptor(draft, pinDescriptors), {
                    actionType: "disconnectPinsByDescriptor",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                });
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },

        connectNetsByDescriptor: (netConnections, portalPart, groundPart, userUid) => {
            if (!netConnections || netConnections.length === 0) {
                return;
            }
            try {
                performDocumentMutation(
                    primitives,
                    (draft) => applyConnectNetsByDescriptor(draft, netConnections, portalPart, groundPart, userUid),
                    {
                        actionType: "connectNetsByDescriptor",
                        canUndo: true,
                        shouldGenerateActionRecord: true,
                        updatesDocumentTimestamp: true,
                    },
                );
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },
    };
}
